import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import type { Article } from '@/data/articles';
import { Clock, User } from 'lucide-react';

interface ArticleCardProps {
  article: Article;
  size?: 'default' | 'compact';
}

export default function ArticleCard({ article, size = 'default' }: ArticleCardProps) {
  return (
    <Link
      href={`/article-detail-page?slug=${article.slug}`}
      className="group block bg-card rounded-lg border border-border card-hover overflow-hidden"
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <AppImage
          src={article.image}
          alt={article.imageAlt}
          fill
          sizes="(max-width: 768px) 100vw, 400px"
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {/* Category tag */}
        <div className="absolute top-3 left-3">
          <span
            className={`text-xs font-700 px-2 py-1 rounded uppercase tracking-wide ${
              article.categoryType === 'hot' ? 'tag-hot' : 'tag-editorial'
            }`}
          >
            {article.subTag}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className={size === 'compact' ? 'p-3' : 'p-4'}>
        <p className="section-eyebrow mb-1.5">{article.category}</p>
        <h3
          className={`font-700 text-foreground leading-snug group-hover:text-primary transition-colors duration-150 ${
            size === 'compact' ? 'text-sm' : 'text-base'
          }`}
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {article.title}
        </h3>
        {size !== 'compact' && (
          <p className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <User size={12} />
            {article.author}
          </span>
          <span>{article.date}</span>
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {article.readTime} min
          </span>
        </div>
      </div>
    </Link>
  );
}